import { useMemo, useState, useCallback } from 'react';
import { 
  useErrorHandlerContext, 
  useUserLanguage, 
  useOpeningElementsControllerContext 
} from '@/customHooks';
import { capitalizeFirstLetter, rentChargeTypeMapper, rentChargeStatusMapper } from '@/helpers';
import { 
  GetRentChargesReportQuery, 
  RentChargesReportFilterInput, 
  useGetRentChargesReportQuery,
  useUpdateRentChargeStatusMutation,
  RentChargeStatusOption
} from '@/graphql/generated';
import { 
  RentChargeStatusOptionPill, 
  LoadingSpinner, 
  PaymentStatusOptionPill 
} from '../atoms';
import { NormalFormSelectionField } from '../molecules';
import textsData from '@/textData/reportTexts.json';
import { GET_RENT_CHARGES_REPORT } from '@/graphql/queries/getRentChargesReport';

type RentChargesForList = NonNullable<GetRentChargesReportQuery['rentChargesReport']>['items'][number];

const readableDate = (date: Date | string | null | undefined, userLanguage: 'es' | 'en') => {
  if (!date) return '-';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString(userLanguage === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '-';
  }
};

const formatAmount = (amount: string, currency: string, userLanguage: 'es' | 'en') => {
  const numAmount = parseFloat(amount);
  if (isNaN(numAmount)) return amount;
  return new Intl.NumberFormat(userLanguage === 'es' ? 'es-ES' : 'en-US', {
    style: 'currency',
    currency: currency || 'USD',
    minimumFractionDigits: 2,
  }).format(numAmount);
};

const renderProviderData = (providerData: any, userLanguage: 'es' | 'en') => {
  if (!providerData) return '-';
  const type = providerData.__typename;
  if (type === 'StripePaymentReportOutput') {
    return `Stripe (ID: ${providerData.operationId})`;
  }
  if (type === 'RedsysPaymentReportOutput') {
    return `Redsys (Ord: ${providerData.order})`;
  }
  if (type === 'DepositPaymentReportOutput') {
    return `${userLanguage === 'es' ? 'Depósito' : 'Deposit'} (${providerData.ownerAccountName} - Ref: ${providerData.reference})`;
  }
  return '-';
};

const localTexts = {
  en: {
    accordion: {
      tenantTitle: 'Tenant Details',
      tenantName: 'Name',
      statusTitle: 'Update Status',
      updateButton: 'Update',
      paymentsTitle: 'Attempted Payments',
      noPayments: 'No recorded payments',
      paymentTable: {
        id: 'ID',
        date: 'Date',
        amount: 'Amount',
        status: 'Status',
        provider: 'Provider'
      }
    },
    confirmation: {
      update: {
        title: 'Rent Charge has been updated',
        description: 'Rent Charge status has been updated successfully',
      }
    }
  },
  es: {
    accordion: {
      tenantTitle: 'Detalles del Inquilino',
      tenantName: 'Nombre',
      statusTitle: 'Actualizar Estado',
      updateButton: 'Actualizar',
      paymentsTitle: 'Pagos Intentados',
      noPayments: 'No hay pagos registrados',
      paymentTable: {
        id: 'ID',
        date: 'Fecha',
        amount: 'Monto',
        status: 'Estado',
        provider: 'Proveedor'
      }
    },
    confirmation: {
      update: {
        title: 'Cargo de Renta actualizado',
        description: 'El estado del cargo de renta ha sido actualizado con éxito',
      }
    }
  }
};

type RentChargesListTableProps = {
  filters: RentChargesReportFilterInput;
};

export const RentChargesListTable = ({ filters }: RentChargesListTableProps) => {
  const userLanguage = useUserLanguage();
  const texts = (userLanguage === 'es' ? textsData.es : textsData.en).rentChargeReport;
  const t = userLanguage === 'es' ? localTexts.es : localTexts.en;

  const { open: errorHandle } = useErrorHandlerContext();
  const { confirmDialogModalOpen } = useOpeningElementsControllerContext();

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<RentChargeStatusOption | null>(null);

  const { data, error, loading } = useGetRentChargesReportQuery({
    variables: {
      filterInput: filters,
    },
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const [updateRentChargeStatus, { loading: updateLoading }] = useUpdateRentChargeStatusMutation({
    refetchQueries: [{ query: GET_RENT_CHARGES_REPORT }]
  });

  const handleUpdateStatus = useCallback(async (id: string) => {
    if (!selectedStatus) return;

    updateRentChargeStatus({
      variables: {
        rentChargeId: id,
        statusOption: selectedStatus,
      }
    })
    .then(() => {
      confirmDialogModalOpen({
        title: t.confirmation.update.title,
        description: t.confirmation.update.description,
      });
    })
    .catch((error) => {
      errorHandle(error);
    });
  }, [selectedStatus, updateRentChargeStatus, confirmDialogModalOpen, errorHandle, t]);

  const toggleExpand = (rentCharge: any) => {
    if (expandedId === rentCharge.id) {
      setExpandedId(null);
      setSelectedStatus(null);
    } else {
      setExpandedId(rentCharge.id);
      setSelectedStatus(rentCharge.rawStatus);
    }
  };

  const dataFlatter = (data: RentChargesForList[] | undefined) => {
    if (!data) return [];

    return data.map((element) => ({
      id: element.id,
      rentChargeType: rentChargeTypeMapper(element.rentChargeTypeOption, userLanguage),
      description: element.description || '-',
      executionDate: element.executionDate,
      dueDate: element.dueDate,
      amount: formatAmount(element.amountValue, element.currencyOption, userLanguage),
      dueAmount: formatAmount(element.amountValue, element.currencyOption, userLanguage),
      status: element.rentChargeStatusOption,
      rawStatus: element.rentChargeStatusOption,
      currency: element.currencyOption,
      tenantName: element.tenant
        ? `${capitalizeFirstLetter(element.tenant.firstName)} ${capitalizeFirstLetter(element.tenant.lastName)}`
        : '-',
      payments: element.payments,
    }));
  };

  const flattenedData = useMemo(
    () => (data ? dataFlatter(data.rentChargesReport?.items) : null),
    [data, userLanguage]
  );

  const statusOptions = useMemo(() => Object.values(RentChargeStatusOption).map(value => ({
    name: rentChargeStatusMapper(value, userLanguage),
    value: value,
  })), [userLanguage]);

  if (error) errorHandle(error);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner text />
      </div>
    );
  }

  if (!flattenedData || flattenedData.length === 0) {
    return (
      <div className="w-full py-4 text-center">
        <span className="font-bold">{texts.noData.table}</span>
      </div>
    );
  }

  return (
    <div className="pt-4 pb-4 rounded-xl grid grid-cols-[1.5fr_1.5fr_2fr_1fr_1fr_1fr_1fr_140px_60px] overflow-x-auto min-w-[1200px]">
      {/* Header */}
      <div className="contents">
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.rentChargeType}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.description}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.tenant}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.executionDate}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.dueDate}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.amount}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.dueAmount}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
          {texts.rentChargesTable.status}
        </div>
        <div className="font-bold text-left p-4 border-t border-b border-gray-400 bg-white">
        </div>
      </div>

      {/* Rows */}
      {flattenedData.map((rentCharge, index) => {
        const rowBg = index % 2 !== 0 ? 'bg-background2' : 'bg-white';
        const isExpanded = expandedId === rentCharge.id;

        return (
          <div key={rentCharge.id} className="contents">
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {rentCharge.rentChargeType}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {rentCharge.description}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {rentCharge.tenantName}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {readableDate(rentCharge.executionDate, userLanguage)}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {readableDate(rentCharge.dueDate, userLanguage)}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {rentCharge.amount}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              {rentCharge.dueAmount}
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center cursor-pointer transition-colors ${rowBg}`}
            >
              <RentChargeStatusOptionPill option={rentCharge.status} />
            </div>
            <div
              onClick={() => toggleExpand(rentCharge)}
              className={`p-4 text-sm border-b border-gray-200 self-stretch flex items-center justify-center cursor-pointer transition-colors ${rowBg}`}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>

            {/* Accordion Content */}
            {isExpanded && (
              <div className="col-start-1 col-end-10 bg-gray-50 pb-6 border-b border-gray-200">
                <div className="bg-white mx-4 mt-2 rounded-lg shadow-sm border border-gray-200 p-6">
                  {/* Top Section: Tenant and Status Update */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                    <div>
                      <h4 className="font-bold text-primary2 mb-4 border-b border-gray-100 pb-2">
                        {t.accordion.tenantTitle}
                      </h4>
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-bold">
                          {t.accordion.tenantName}
                        </span>
                        <span className="text-sm font-medium text-gray-700">
                          {rentCharge.tenantName}
                        </span>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-bold text-primary2 mb-4 border-b border-gray-100 pb-2">
                        {t.accordion.statusTitle}
                      </h4>
                      <div className="flex items-end gap-4 max-w-md">
                        <div className="flex-1">
                          <NormalFormSelectionField<RentChargeStatusOption>
                            options={statusOptions}
                            name="status"
                            value={selectedStatus}
                            label={userLanguage === 'es' ? 'Estado' : 'Status'}
                            onChange={(value) => setSelectedStatus(value)}
                          />
                        </div>
                        <button
                          disabled={updateLoading || !selectedStatus}
                          onClick={() => handleUpdateStatus(rentCharge.id)}
                          className="bg-primary1 text-white text-xs font-bold h-[42px] px-6 rounded hover:opacity-90 transition-opacity disabled:opacity-50 mb-[2px]"
                        >
                          {updateLoading ? (userLanguage === 'es' ? 'Cargando...' : 'Loading...') : t.accordion.updateButton}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Section: Payments List */}
                  <div>
                    <h4 className="font-bold text-primary2 mb-4 border-b border-gray-100 pb-2">
                      {t.accordion.paymentsTitle}
                    </h4>
                    {rentCharge.payments && rentCharge.payments.length > 0 ? (
                      <div className="overflow-x-auto border border-gray-100 rounded-lg">
                        <table className="w-full text-left border-collapse">
                          <thead>
                            <tr className="bg-gray-50">
                              <th className="p-3 text-[10px] text-gray-400 uppercase tracking-wider font-bold border-b">{t.accordion.paymentTable.id}</th>
                              <th className="p-3 text-[10px] text-gray-400 uppercase tracking-wider font-bold border-b">{t.accordion.paymentTable.date}</th>
                              <th className="p-3 text-[10px] text-gray-400 uppercase tracking-wider font-bold border-b">{t.accordion.paymentTable.amount}</th>
                              <th className="p-3 text-[10px] text-gray-400 uppercase tracking-wider font-bold border-b">{t.accordion.paymentTable.status}</th>
                              <th className="p-3 text-[10px] text-gray-400 uppercase tracking-wider font-bold border-b">{t.accordion.paymentTable.provider}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rentCharge.payments.map((payment: any) => (
                              <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                                <td className="p-3 text-sm text-gray-600 border-b truncate max-w-[120px]" title={payment.id}>
                                  {payment.id}
                                </td>
                                <td className="p-3 text-sm text-gray-600 border-b">
                                  {readableDate(payment.createdAt, userLanguage)}
                                </td>
                                <td className="p-3 text-sm font-bold text-primary1 border-b">
                                  {formatAmount(payment.amountValue, rentCharge.currency, userLanguage)}
                                </td>
                                <td className="p-3 text-sm border-b">
                                  <PaymentStatusOptionPill option={payment.paymentStatusOption} />
                                </td>
                                <td className="p-3 text-sm text-gray-600 border-b">
                                  {renderProviderData(payment.providerData, userLanguage)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic py-4">
                        {t.accordion.noPayments}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};


